import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuthStore, useExamStore } from '@/stores';
import { useMotionSafe } from '@/utils/motion';

const ExamCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const { pageEnter, fadeRise } = useMotionSafe();
  const user = useAuthStore(state => state.user);
  const score = useExamStore(state => state.score);
  const correctAnswers = useExamStore(state => state.correctAnswers);
  const totalQuestions = useExamStore(state => state.totalQuestions);
  const resetExam = useExamStore(state => state.resetExam);

  const returnHome = () => {
    resetExam();
    navigate('/');
  };

  return (
    <motion.main
      className="flex min-h-screen items-center bg-surface-canvas px-4 py-12 font-sans text-content-primary sm:px-6"
      {...pageEnter}
    >
      <motion.section
        className="mx-auto w-full max-w-3xl border-y border-hairline py-10 text-center sm:py-14"
        {...fadeRise}
      >
        <div className="mx-auto mb-6 flex size-14 items-center justify-center rounded-card border border-state-nominal/25 bg-state-nominal-soft text-state-nominal">
          <Check className="size-7" strokeWidth={2} aria-hidden="true" />
        </div>

        <p className="text-caption font-medium uppercase tracking-[0.16em] text-content-muted">
          Submission received
        </p>
        <h1 className="mt-3 font-display text-heading font-normal">Exam complete</h1>
        <p className="mx-auto mt-4 max-w-xl leading-7 text-content-secondary">
          {user?.name ? `${user.name}, your` : 'Your'} responses have been recorded and the
          monitoring session is closed.
        </p>

        {score !== null ? (
          <dl className="mx-auto mt-10 grid max-w-xl grid-cols-1 border-l border-t border-subtle sm:grid-cols-2">
            <div className="border-b border-r border-subtle bg-surface-card p-6">
              <dt className="text-sm text-content-muted">Final score</dt>
              <dd className="mt-2 font-display text-heading font-normal tabular-nums">
                {score.toFixed(1)}%
              </dd>
            </div>
            <div className="border-b border-r border-subtle bg-surface-card p-6">
              <dt className="text-sm text-content-muted">Correct answers</dt>
              <dd className="mt-2 font-display text-heading font-normal tabular-nums">
                {correctAnswers ?? 0} / {totalQuestions}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="mx-auto mt-10 max-w-xl rounded-control border border-subtle bg-surface-nested p-5 text-sm text-content-secondary">
            Results are unavailable after a page refresh, but your submission remains recorded.
          </div>
        )}

        <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
          <Button variant="primary" size="lg" onClick={returnHome}>
            Return home
          </Button>
        </div>
      </motion.section>
    </motion.main>
  );
};

export default ExamCompletePage;
